import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Autocomplete, debounce, MenuItem, TextField } from '@mui/material';
import { useLocales, useTheme } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller, useWatch } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { markAsRequired } from '../../../../utils/formHelpers';
import FormControl from '../../FormControl';
import { EnrichForm } from './Enrich';
import { useData } from '../../../../data-layer';
import { MediaResponse } from '../../../../API/model/media-response';
import { enrichTestIds } from '../../TestsIds';
import { SmartQuerySearchIndex } from '../../../../API/model/smart-query-search-index';
import { SmartQueryOperatorSymbol } from '../../../../API/model/smart-query-operator-symbol';
import {
  RedirectionType as EnrichRedirectionType,
  SmartQueryFieldSymbol,
  SmartQuerySortingDirection,
  SmartQuerySortingSymbol
} from '../../../../API';

interface DeepLinkContentProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
  type: EnrichRedirectionType;
}

export const useStyles = makeStyles()((theme) => ({
  autocomplete: {
    minWidth: 360
  },
  option: {
    padding: `${theme.spacing(4)} !important`,
    overflow: 'hidden',
    flexWrap: 'wrap'
  },
  input: {
    width: '100%'
  }
}));

const getIndex = (type: EnrichRedirectionType) => {
  if (type === EnrichRedirectionType.VOD) return SmartQuerySearchIndex.CONTENT;
  if (type === EnrichRedirectionType.LIVE) return SmartQuerySearchIndex.TRANSMISSION;

  return SmartQuerySearchIndex.CONTENT;
};

export function DeepLinkContentField({ control, isSaving, type }: DeepLinkContentProps): JSX.Element | null {
  const { classes, cx } = useStyles();
  const { formControlColor } = useTheme();
  const { t, localize } = useLocales();
  const [entities, setEntities] = useState<MediaResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>();
  const searchIndex = getIndex(type);

  const {
    media: {
      state: { withRecordById },
      hook: { getMediaBySmartQuery, queueIdToFetch }
    }
  } = useData();

  const selectedId = useWatch({ control, name: 'redirection.value' });
  const selectedEntity = useRecoilValue(withRecordById(selectedId));
  const options =
    selectedEntity && !entities.some(({ contentId }) => contentId === selectedEntity.contentId)
      ? [selectedEntity, ...entities]
      : entities;

  useEffect(() => {
    debouncedGetMedia(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (selectedId && !selectedEntity) {
      queueIdToFetch(selectedId);
    }
  }, [selectedId, selectedEntity]);

  const debouncedGetMedia = useCallback(
    debounce(async (inputValue: string | undefined) => {
      if (!inputValue) return;
      setIsLoading(true);

      const response = await getMediaBySmartQuery({
        searchIndex,
        ...getQueryConditions(searchIndex, inputValue)
      });
      setEntities(response ?? []);
      setIsLoading(false);
    }, 500),
    [searchIndex]
  );

  const onValueChange = (
    event: SyntheticEvent,
    changedValue: MediaResponse | null,
    onChange: (value: string) => void
  ) => {
    if (event && event.type === 'click') {
      onChange(changedValue?.contentId ?? '');
    }
  };

  const onInputChange = (event: SyntheticEvent, newValue: string) => {
    if (!event) return;
    if (event.type === 'change' && newValue !== inputValue) {
      setInputValue(newValue);
    } else {
      setInputValue('');
    }
  };

  return (
    <Controller
      control={control}
      rules={{ required: t('general.field_is_required') }}
      name="redirection.value"
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormControl>
          <Autocomplete
            role="listbox"
            options={options}
            clearOnBlur
            blurOnSelect
            loading={isLoading}
            className={classes.autocomplete}
            filterOptions={(options) => options}
            getOptionLabel={(option) => localize(option.title)}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                className={cx(props.className, classes.option)}
                key={option?.contentId || 'none'}
                data-option={option?.contentId || null}
                data-testid={enrichTestIds.deepLinkContentFieldOption}
              >
                <div>{localize(option.title)}</div>
              </MenuItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                className={classes.input}
                name="collection"
                label={markAsRequired(t('content.drawer.deep_link.autocomplete_label'), true)}
                color={formControlColor}
                error={!!error}
                helperText={error?.message}
              />
            )}
            value={options.find((entity) => entity.contentId === value) || null}
            onChange={(event, changedValue) => onValueChange(event, changedValue, onChange)}
            onInputChange={onInputChange}
            isOptionEqualToValue={(option, value) => option.contentId === value.contentId}
            data-testid={enrichTestIds.deepLinkContentFieldAutocomplete}
            disabled={isSaving}
          />
        </FormControl>
      )}
    />
  );
}

const getQueryConditions = (searchIndex: SmartQuerySearchIndex, inputValue: string) => {
  if (searchIndex === SmartQuerySearchIndex.TRANSMISSION) {
    return {
      conditions: [
        {
          key: '0',
          operator: SmartQueryOperatorSymbol.CONTAINS,
          field: SmartQueryFieldSymbol.TITLE,
          value: inputValue
        }
      ],
      sortings: []
    };
  }

  // CONTENT
  return {
    conditions: [
      {
        key: '0',
        operator: SmartQueryOperatorSymbol.OR
      },
      {
        key: '0.0',
        operator: SmartQueryOperatorSymbol.CONTAINS,
        field: SmartQueryFieldSymbol.KEYWORDS,
        value: inputValue
      },
      {
        key: '0.1',
        operator: SmartQueryOperatorSymbol.CONTAINS,
        field: SmartQueryFieldSymbol.TITLE,
        value: inputValue
      },
      {
        key: '0.2',
        operator: SmartQueryOperatorSymbol.CONTAINS,
        field: SmartQueryFieldSymbol.DESCRIPTION,
        value: inputValue
      }
    ],
    sortings: [{ field: SmartQuerySortingSymbol.DATE_MODIFIED, direction: SmartQuerySortingDirection.DESC }]
  };
};
